import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, ClipboardList, Calendar, CheckCircle2, Loader2, Navigation } from 'lucide-react';
import EstimateQuiz from './EstimateQuiz';
import { submitToWeb3Forms } from '../lib/web3forms';

const SERVICE_TYPES = [
  'Residential Cleaning',
  'Commercial / Office Cleaning',
  'Carpet Cleaning',
  'Pressure Washing',
  'Move-In / Move-Out',
  'Disinfection Services',
  'Other',
];

export default function Contact() {
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: '-100px' });
  const [mode, setMode] = useState<'form' | 'estimate'>('form');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    serviceType: SERVICE_TYPES[0],
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const updateField = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    const ok = await submitToWeb3Forms(`New Contact Form Submission - ${formData.firstName} ${formData.lastName}`, {
      'First Name': formData.firstName,
      'Last Name': formData.lastName,
      Email: formData.email,
      Phone: formData.phone,
      Address: formData.address,
      'Service Type': formData.serviceType,
      Message: formData.message,
    });

    if (ok) {
      setStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        serviceType: SERVICE_TYPES[0],
        message: '',
      });
    } else {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-white overflow-hidden">
      {/* Background Decorative */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl">
        <div className="absolute top-20 right-0 w-96 h-96 bg-axis-50 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 left-0 w-72 h-72 bg-sand-50 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto section-padding relative z-10">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-axis-50 rounded-full px-4 py-1.5 mb-6">
            <span className="text-sm font-medium text-axis-700">Get In Touch</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-axis-950 leading-tight mb-4">
            Ready for a
            <br />
            <span className="gradient-text italic">Cleaner Space?</span>
          </h2>
          <p className="text-gray-500 text-lg">
            Contact us today for a free estimate and let us bring freshness to your home or office.
          </p>
        </motion.div>

        {/* Mode Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center mb-10"
        >
          <div className="inline-flex items-center gap-1 bg-sand-50 border border-sand-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => setMode('form')}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                mode === 'form' ? 'bg-white text-axis-900 shadow-sm' : 'text-gray-500 hover:text-axis-700'
              }`}
            >
              <Send className="w-4 h-4" />
              Contact Form
            </button>
            <button
              type="button"
              onClick={() => setMode('estimate')}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                mode === 'estimate' ? 'bg-white text-axis-900 shadow-sm' : 'text-gray-500 hover:text-axis-700'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              Free Estimate
            </button>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Form / Free Estimate Quiz */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3"
          >
            {mode === 'estimate' ? (
              <EstimateQuiz />
            ) : (
            <div className="bg-sand-50 rounded-3xl p-8 lg:p-10 border border-sand-100">
              <h3 className="font-display text-2xl font-semibold text-axis-900 mb-6">
                Request a Service
              </h3>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center text-center py-10">
                  <div className="w-16 h-16 rounded-full clean-gradient flex items-center justify-center mb-6 shadow-lg shadow-axis-500/20">
                    <CheckCircle2 className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-display text-xl font-semibold text-axis-900 mb-2">Thank you!</h4>
                  <p className="text-gray-600 max-w-sm mb-6">
                    Your request has been sent — our team will reach out shortly. You can also reach us directly at
                    240-750-9535.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="text-sm font-semibold text-axis-600 hover:text-axis-700 transition-colors"
                  >
                    Send another request
                  </button>
                </div>
              ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={updateField('firstName')}
                      placeholder="Your first name"
                      className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={updateField('lastName')}
                      placeholder="Your last name"
                      className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={updateField('email')}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={updateField('phone')}
                      placeholder="(240) 000-0000"
                      className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                  <input
                    type="text"
                    required
                    value={formData.address}
                    onChange={updateField('address')}
                    placeholder="Street address, city, state"
                    className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Service Type</label>
                  <select
                    value={formData.serviceType}
                    onChange={updateField('serviceType')}
                    className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all appearance-none cursor-pointer"
                  >
                    {SERVICE_TYPES.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={updateField('message')}
                    placeholder="Tell us about your cleaning needs..."
                    className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all resize-none"
                  />
                </div>
                {status === 'error' && (
                  <p className="text-sm text-red-500">
                    Something went wrong sending your request. Please try again or call us at 240-750-9535.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full clean-gradient text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg shadow-axis-500/20 hover:shadow-axis-500/40 hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Request
                    </>
                  )}
                </button>
              </form>
              )}
            </div>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Phone Card */}
            <a
              href="tel:2407509535"
              className="group flex items-start gap-4 bg-sand-50 rounded-2xl p-6 border border-sand-100 hover:shadow-lg hover:shadow-axis-500/5 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl clean-gradient flex items-center justify-center flex-shrink-0 shadow-md shadow-axis-500/20 group-hover:shadow-axis-500/30">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-display text-base font-semibold text-axis-900 mb-0.5">Call Us</h4>
                <p className="text-axis-600 font-medium">240-750-9535</p>
                <p className="text-xs text-gray-500 mt-1">Available during business hours</p>
              </div>
            </a>

            {/* Email Card */}
            <a
              href="mailto:info@axiscleaning.com"
              className="group flex items-start gap-4 bg-sand-50 rounded-2xl p-6 border border-sand-100 hover:shadow-lg hover:shadow-axis-500/5 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl clean-gradient flex items-center justify-center flex-shrink-0 shadow-md shadow-axis-500/20 group-hover:shadow-axis-500/30">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-display text-base font-semibold text-axis-900 mb-0.5">Email Us</h4>
                <p className="text-axis-600 font-medium">info@axiscleaning.com</p>
                <p className="text-xs text-gray-500 mt-1">We typically respond within 24 hours</p>
              </div>
            </a>

            {/* Address Card */}
            <div className="bg-sand-50 rounded-2xl p-6 border border-sand-100">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-sand-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-sand-600" />
                </div>
                <div>
                  <h4 className="font-display text-base font-semibold text-axis-900 mb-0.5">Located At</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    481 N Frederick Ave, Suite 412
                    <br />
                    Gaithersburg, MD 20877
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-sand-200 mb-3">
                <iframe
                  title="AXIS Cleaning Services location map"
                  src="https://www.google.com/maps?q=481+N+Frederick+Ave+Suite+412+Gaithersburg+MD+20877&output=embed"
                  width="100%"
                  height="160"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=481+N+Frederick+Ave+Suite+412+Gaithersburg+MD+20877"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-axis-600 font-semibold text-sm hover:text-axis-700 transition-colors"
              >
                <Navigation className="w-4 h-4" />
                Get Directions
              </a>
            </div>

            {/* Service Areas */}
            <div className="bg-axis-950 rounded-2xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-axis-300" />
                </div>
                <div>
                  <h4 className="font-display text-base font-semibold mb-1">Service Areas</h4>
                  <p className="text-axis-300/70 text-sm leading-relaxed">
                    Montgomery County and surrounding areas, including Germantown, MD
                  </p>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="bg-sand-50 rounded-2xl p-6 border border-sand-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-axis-50 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-axis-600" />
                </div>
                <div>
                  <h4 className="font-display text-base font-semibold text-axis-900 mb-1">Scheduling</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Weekly, Bi-Weekly & Monthly
                    <br />
                    Custom scheduling available
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
